import * as THREE from 'three';
import { AIM_PRESETS, AIM_PRESET_LABELS, type AimPreset } from '../combat/autoaim';

export interface OverlayStats {
  renderer: THREE.WebGLRenderer;
  getEntityCount: () => number;
  getAimPreset: () => AimPreset;
  onAimPresetChange: (preset: AimPreset) => void;
}

/**
 * S12 gate-pass addition: the structured equivalent of the overlay's own
 * `statsEl.textContent` string, for a scripted soak (Playwright or a real
 * on-device automation) to read numerically instead of parsing display text.
 * Values mirror `render()`'s own locals exactly — this is a read of the SAME
 * computation, not a second independent one, so it can never disagree with
 * what's on screen.
 */
export interface OverlaySnapshot {
  fps: number;
  worstFps: number | null;
  drawCalls: number;
  peakDrawCalls: number;
  triangles: number;
  peakTriangles: number;
  heapMb: number | null;
  peakHeapMb: number;
  entities: number;
  /** S12 spec-fidelity review fix: the same §3 thresholds the on-screen `!OVER` flags below compare against, exported as data — a soak script reading `OverlaySnapshot` numbers now has no reason to hand-copy these 4 constants from a second source (exactly the hardcoded-duplicate-constant drift class this project has hit and fixed before, e.g. S02's AIM_RANGE, S04's VERTICAL_ENGAGEMENT_RANGE). */
  budgets: { minFps: number; maxDrawCalls: number; maxTriangles: number; maxHeapMb: number };
}

const SAMPLE_WINDOW = 30;

// S08 (PLAN-CITY §3) — the hard, on-device gates. Informational here (this
// overlay can't know if it's running on the named test phone or a dev
// desktop), but flagging a live breach is exactly the "on-device soak vs §3
// budgets" instrument S08 owns and S12's own 20-minute soak session needs to
// exist before it can run. Session-lifetime PEAK tracking (not just the
// live instantaneous reading) matters because a budget breach during one
// bad frame of a boss fight is the failure mode that actually matters for
// "never < 45fps during boss + 12 enemies + FX" — an instantaneous reading
// sampled at a random moment could easily miss it.
const BUDGET_MIN_FPS = 45;
const BUDGET_MAX_DRAW_CALLS = 90;
const BUDGET_MAX_TRIANGLES = 250_000;
const BUDGET_MAX_HEAP_MB = 300;

/**
 * `?dev=1` overlay: FPS/ms, draw calls, tris, heap, entity count, plus two
 * named slots from PLAN-CITY §2 — an aim-assist preset switcher (S02: a real
 * live selector now, a secondary/verification-only path alongside the
 * player-facing HUD cycle button in ui/hud.ts) and teleport (live in S00:
 * tap the right half of the screen to raycast-teleport, wired in main.ts
 * since it needs the camera/scene/player references this module doesn't
 * own). Zero footprint when ?dev=1 is absent — nothing here is constructed
 * otherwise.
 *
 * S08 added session-lifetime peak/worst tracking against §3's hard budgets
 * (BUDGET_* above) — the on-device instrument a 20-minute soak (S12's own
 * gate) reads from, since a live-only number can't answer "did this EVER
 * breach budget during the session," only "is it fine right now."
 */
export class DevOverlay {
  readonly el: HTMLDivElement;
  private readonly statsEl: HTMLDivElement;
  private readonly aimSelect: HTMLSelectElement;
  private readonly frameTimes: number[] = [];
  private lastNow = performance.now();
  private worstFps = Infinity;
  private peakDrawCalls = 0;
  private peakTriangles = 0;
  private peakHeapMb = 0;
  private lastSnapshot: OverlaySnapshot | null = null;

  constructor(private readonly stats: OverlayStats) {
    this.el = document.createElement('div');
    this.el.className = 'br3d-overlay';

    this.statsEl = document.createElement('div');
    this.el.append(this.statsEl);

    this.aimSelect = document.createElement('select');
    this.aimSelect.id = 'br3d-aim-select';
    for (const preset of AIM_PRESETS) {
      const opt = document.createElement('option');
      opt.value = preset;
      opt.textContent = `aim-assist: ${AIM_PRESET_LABELS[preset]}`;
      this.aimSelect.append(opt);
    }
    this.aimSelect.value = stats.getAimPreset();
    this.aimSelect.addEventListener('change', () => stats.onAimPresetChange(this.aimSelect.value as AimPreset));
    this.el.append(this.aimSelect);

    document.body.append(this.el);
  }

  recordFrame(): void {
    const now = performance.now();
    // Clamp: a tab returning from background (or the loop's own visibility
    // pause) produces one huge dt sample that would otherwise drag the
    // rolling FPS average down to ~1 for the next SAMPLE_WINDOW frames.
    const dt = Math.min(now - this.lastNow, 200);
    this.lastNow = now;
    this.frameTimes.push(dt);
    if (this.frameTimes.length > SAMPLE_WINDOW) this.frameTimes.shift();
  }

  render(): void {
    // The player-facing HUD's own cycle button (ui/hud.ts) can change the
    // preset outside this dropdown entirely — resync the displayed value
    // every frame so the two controls never silently disagree.
    const live = this.stats.getAimPreset();
    if (this.aimSelect.value !== live) this.aimSelect.value = live;
    const avgMs = this.frameTimes.reduce((a, b) => a + b, 0) / (this.frameTimes.length || 1);
    const fps = avgMs > 0 ? 1000 / avgMs : 0;
    const info = this.stats.renderer.info;
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    const heapMb = mem ? mem.usedJSHeapSize / 1048576 : null;

    // Only start tracking the session-worst FPS once the rolling window has
    // actually filled — the first ~SAMPLE_WINDOW frames (asset loads, JIT
    // warmup) read artificially low and would otherwise permanently poison
    // "worst FPS this session" with a number that has nothing to do with
    // real steady-state perf.
    if (this.frameTimes.length >= SAMPLE_WINDOW && fps > 0 && fps < this.worstFps) this.worstFps = fps;
    if (info.render.calls > this.peakDrawCalls) this.peakDrawCalls = info.render.calls;
    if (info.render.triangles > this.peakTriangles) this.peakTriangles = info.render.triangles;
    if (heapMb !== null && heapMb > this.peakHeapMb) this.peakHeapMb = heapMb;

    const flag = (bad: boolean): string => (bad ? ' !OVER' : '');
    const worstFpsText = Number.isFinite(this.worstFps) ? this.worstFps.toFixed(0) : '--';

    this.statsEl.textContent =
      `FPS ${fps.toFixed(0)} (${avgMs.toFixed(1)}ms)  worst ${worstFpsText}${flag(Number.isFinite(this.worstFps) && this.worstFps < BUDGET_MIN_FPS)}\n` +
      `draws ${info.render.calls} peak ${this.peakDrawCalls}${flag(this.peakDrawCalls > BUDGET_MAX_DRAW_CALLS)}  ` +
      `tris ${info.render.triangles} peak ${this.peakTriangles}${flag(this.peakTriangles > BUDGET_MAX_TRIANGLES)}\n` +
      `heap ${heapMb !== null ? heapMb.toFixed(1) : 'n/a'}MB peak ${this.peakHeapMb.toFixed(1)}${flag(this.peakHeapMb > BUDGET_MAX_HEAP_MB)}  entities ${this.stats.getEntityCount()}\n` +
      `tap right half to teleport`;

    this.lastSnapshot = {
      fps,
      worstFps: Number.isFinite(this.worstFps) ? this.worstFps : null,
      drawCalls: info.render.calls,
      peakDrawCalls: this.peakDrawCalls,
      triangles: info.render.triangles,
      peakTriangles: this.peakTriangles,
      heapMb,
      peakHeapMb: this.peakHeapMb,
      entities: this.stats.getEntityCount(),
      budgets: { minFps: BUDGET_MIN_FPS, maxDrawCalls: BUDGET_MAX_DRAW_CALLS, maxTriangles: BUDGET_MAX_TRIANGLES, maxHeapMb: BUDGET_MAX_HEAP_MB },
    };
  }

  /** S12: the last frame's numbers as data, not display text — see `OverlaySnapshot`'s own doc comment. `null` until the first `render()` call (never, in practice: main.ts calls it every frame once the overlay exists). */
  getSnapshot(): OverlaySnapshot | null {
    return this.lastSnapshot;
  }
}
